import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { CharlesProvider } from "@/contexts/CharlesContext";
import { ConciergeAdvisor } from "@/components/concierge/ConciergeAdvisor";
import { ResourceSection } from "@/components/concierge/ResourceSection";
import { ConsentGate } from "@/components/concierge/ConsentGate";
import { ConsentWithdrawPanel } from "@/components/concierge/ConsentWithdrawPanel";
import { ConciergeHeader } from "@/components/concierge/ConciergeHeader";
import {
  useConciergeResources,
  useMyConciergeSite,
  useConciergeSiteById,
} from "@/lib/concierge/api";
import { supabase } from "@/lib/supabase";
import { emitConciergeEvent } from "@/lib/concierge/events";
import { useMyConsent, isActive } from "@/lib/concierge/consent";

const RESOURCE_ORDER = ["video", "report", "podcast", "link"] as const;

export default function ConciergeHome() {
  const [params] = useSearchParams();
  const previewSiteId = params.get("siteId") || undefined;
  const previewMode = !!previewSiteId;

  const my = useMyConciergeSite();
  const previewQ = useConciergeSiteById(previewSiteId);
  const site = previewMode ? previewQ.data : my.data;
  const siteLoading = previewMode ? previewQ.isLoading : my.isLoading;


  const { data: coachHandle } = useQuery({
    queryKey: ["concierge", "coach-handle", site?.id],
    queryFn: async () => {
      const { data } = await (supabase as any).rpc("get_my_concierge_coach_handle", {
        _site_id: site!.id,
      });
      return (data as string | null) ?? null;
    },
    enabled: !!site?.id,
    staleTime: 60_000,
  });

  if (siteLoading) {
    return (
      <div className="flex items-center justify-center min-h-[100dvh]">
        <Loader2 className="h-5 w-5 animate-spin opacity-60" />
      </div>
    );
  }

  if (!site) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center space-y-3">
        <h1 className="text-2xl font-heading font-semibold tracking-tight">
          {previewMode ? "Concierge preview unavailable" : "Your concierge isn't set up yet"}
        </h1>
        <p className="text-muted-foreground">
          {previewMode
            ? "You don't have access to preview this site, or it no longer exists."
            : "When your team publishes a concierge experience for you, it will appear here."}
        </p>
      </div>
    );
  }

  if (previewMode) {
    return <ConciergePreview site={site} />;
  }

  return (
    <ConsentGate site={site} coachHandle={coachHandle}>
      <ConciergeConsented />
    </ConsentGate>
  );
}

function ConciergePreview({ site }: { site: NonNullable<ReturnType<typeof useMyConciergeSite>["data"]> }) {
  const { data: resources = [], isLoading: resLoading } = useConciergeResources(site.id);
  const grouped: Record<string, typeof resources> = {};
  for (const r of resources) {
    (grouped[r.type] ||= []).push(r);
  }
  return (
    <CharlesProvider siteId={site.id} agentIdOverride={site.elevenlabs_agent_id ?? undefined}>
      <div className="max-w-5xl mx-auto px-8 py-16 space-y-16">
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm text-amber-200">
          Admin preview — engagement events are not recorded.
        </div>
        <ConciergeHeader title={site.title || "Your Concierge"} advisorName={site.advisor_name} />
        <ConciergeAdvisor advisorName={site.advisor_name} siteId={site.id} />
        {resLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin opacity-60" />
          </div>
        ) : (
          <div className="space-y-14">
            {RESOURCE_ORDER.map((t) => (
              <ResourceSection key={t} type={t} resources={grouped[t] ?? []} siteId={site.id} />
            ))}
          </div>
        )}
      </div>
    </CharlesProvider>
  );
}

function ConciergeConsented() {
  const { data: site } = useMyConciergeSite();

  const { data: resources = [], isLoading: resLoading } = useConciergeResources(site?.id);
  const { data: consent } = useMyConsent(site?.id);

  useEffect(() => {
    if (site?.id && isActive(consent)) {
      emitConciergeEvent(site.id, "page_view");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?.id]);

  if (!site) return null;

  const grouped: Record<string, typeof resources> = {};
  for (const r of resources) {
    (grouped[r.type] ||= []).push(r);
  }

  return (
    <CharlesProvider
      siteId={site.id}
      agentIdOverride={site.elevenlabs_agent_id ?? undefined}
    >
      <div className="max-w-5xl mx-auto px-8 py-16 space-y-16">
        <ConciergeHeader
          title={site.title || "Your Concierge"}
          advisorName={site.advisor_name}
        />

        <ConciergeAdvisor advisorName={site.advisor_name} siteId={site.id} />

        {resLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin opacity-60" />
          </div>
        ) : (
          <div className="space-y-14">
            {RESOURCE_ORDER.map((t) => (
              <ResourceSection key={t} type={t} resources={grouped[t] ?? []} siteId={site.id} />
            ))}
          </div>
        )}

        <ConsentWithdrawPanel siteId={site.id} />
      </div>
    </CharlesProvider>
  );
}
